// Clipboard image helpers — shared by the Instagram Post Maker (user) and Admin Templates.
// Converts clipboard image data into a standard File so it flows through the EXISTING upload pipeline.

export const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const EXT = { "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp" };

export function isSupportedImage(type) {
  return ALLOWED_IMAGE_TYPES.includes(type);
}

export function blobToFile(blob, prefix = "clipboard") {
  const ext = EXT[blob.type] || "png";
  return new File([blob], `${prefix}-${Date.now()}.${ext}`, { type: blob.type });
}

// Attempt to read an image from the clipboard via the async Clipboard API.
// Returns a File. Throws Error (with .code) describing why it failed.
export async function readClipboardImageFile() {
  if (!navigator.clipboard || !navigator.clipboard.read) {
    const e = new Error("Clipboard access isn't available. Copy an image and press Ctrl+V / Cmd+V to paste it.");
    e.code = "no_api"; throw e;
  }
  let items;
  try {
    items = await navigator.clipboard.read();
  } catch {
    const e = new Error("Clipboard access isn't available. Copy an image and press Ctrl+V / Cmd+V to paste it.");
    e.code = "denied"; throw e;
  }
  for (const item of items) {
    const imgType = item.types.find((t) => t.startsWith("image/"));
    if (imgType) {
      const blob = await item.getType(imgType);
      if (!isSupportedImage(blob.type)) {
        const e = new Error("Clipboard content isn't a supported image."); e.code = "unsupported"; throw e;
      }
      return blobToFile(blob);
    }
  }
  const e = new Error("Please copy an image to your clipboard."); e.code = "no_image"; throw e;
}

// Extract an image File from a native paste ClipboardEvent (fallback path).
// Returns File, or null when the paste has no image (e.g. plain text — let it flow normally).
// Throws Error only when clipboard has image data of an unsupported type.
export function imageFileFromPasteEvent(event) {
  const items = event.clipboardData?.items;
  if (!items) return null;
  for (const it of items) {
    if (it.kind === "file" && it.type.startsWith("image/")) {
      const blob = it.getAsFile();
      if (!blob) continue;
      if (!isSupportedImage(blob.type)) {
        const e = new Error("Clipboard content isn't a supported image."); e.code = "unsupported"; throw e;
      }
      return blobToFile(blob);
    }
  }
  return null;
}
